
import { Component, Prop, Vue } from 'vue-property-decorator';

/**
 * NavigationGuard Component
 * 
 * Kullanım:
 * <navigation-guard :is-blocked="isSubmitting">
 *   <your-content />
 * </navigation-guard>
 * 
 * Props:
 * - isBlocked: boolean - İşlem devam ederken true olmalı
 * - dialogTitle: string - Dialog başlığı (opsiyonel)
 * - dialogMessage: string - Dialog mesajı (opsiyonel)
 * - confirmButtonText: string - Onay butonu metni (opsiyonel)
 * - cancelButtonText: string - İptal butonu metni (opsiyonel)
 * - enableBeforeUnload: boolean - Sayfa yenileme/kapatma engellemesi (default: true)
 * 
 * Events:
 * - navigation-blocked: Navigasyon engellendiğinde emit edilir
 * - navigation-allowed: Kullanıcı navigasyona izin verdiğinde emit edilir
 */
@Component({
  name: 'NavigationGuard'
})
export default class NavigationGuard extends Vue {
  @Prop({ type: Boolean, required: true })
  isBlocked!: boolean;

  @Prop({ type: String, default: 'İşlem Devam Ediyor' })
  dialogTitle!: string;

  @Prop({ type: String, default: 'İşlem devam ediyor. Sayfadan ayrılırsanız işlem yarıda kalabilir.' })
  dialogMessage!: string;

  @Prop({ type: String, default: 'Ayrıl' })
  confirmButtonText!: string;

  @Prop({ type: String, default: 'Devam Et' })
  cancelButtonText!: string;

  @Prop({ type: Boolean, default: true })
  enableBeforeUnload!: boolean;

  // Local state
  showDialog: boolean = false;
  isConfirmDisabled: boolean = false;
  private navigationCallback: ((proceed: boolean) => void) | null = null;
  private unregisterRouterGuard?: () => void;
  private confirmTimeout: any = null;

  mounted() {
    // Browser seviyesinde sayfa yenileme/kapatma engellemesi
    if (this.enableBeforeUnload) {
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    // Router seviyesinde navigasyon engellemesi
    this.unregisterRouterGuard = this.$router.beforeEach((to, from, next) => {
      if (this.isBlocked) {
        // Custom dialog göster
        this.isConfirmDisabled = true;
        if (this.confirmTimeout) clearTimeout(this.confirmTimeout);
        this.confirmTimeout = setTimeout(() => {
          this.isConfirmDisabled = false;
        }, 3000);

        this.showDialog = true;
        this.$emit('navigation-blocked', { to, from });
        
        this.navigationCallback = (proceed: boolean) => {
          if (proceed) {
            this.$emit('navigation-allowed', { to, from });
            next();
          } else {
            next(false);
          }
        };
      } else {
        next();
      }
    });
  }

  beforeDestroy() {
    // Event listener'ı temizle
    if (this.enableBeforeUnload) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    if (this.confirmTimeout) {
      clearTimeout(this.confirmTimeout);
    }
    
    // Router guard'ı temizle
    if (this.unregisterRouterGuard) {
      this.unregisterRouterGuard();
    }
  }

  /**
   * Browser seviyesinde sayfa yenileme/kapatma engellemesi
   */
  handleBeforeUnload(e: BeforeUnloadEvent) {
    if (this.isBlocked) {
      e.preventDefault();
      e.returnValue = ''; // Chrome için gerekli
      return ''; // Eski tarayıcılar için
    }
  }

  /**
   * Dialog - Devam Et (işleme devam et, sayfadan ayrılma)
   */
  handleCancel() {
    this.showDialog = false;
    if (this.navigationCallback) {
      this.navigationCallback(false); // Navigasyonu iptal et
      this.navigationCallback = null;
    }
  }

  /**
   * Dialog - Ayrıl (sayfadan ayrıl)
   */
  handleConfirm() {
    this.showDialog = false;
    this.$emit('cancel-operation');
    if (this.navigationCallback) {
      this.navigationCallback(true); // Navigasyona izin ver
      this.navigationCallback = null;
    }
  }
}
